import {ApiUrls} from 'shared/config/model/urls';

import {SubmitHandler, useForm, UseFormReturn} from 'react-hook-form';
import {useDispatch, useSelector} from 'react-redux';

import {tagsAPI, useBatchOperation, useBatchResult, IBatchResult} from 'entities/monitor';
import {domainMonitorAPI} from 'features/domain/api/domainMonitorAPI';
import {buildDomainMonitorSettings, getDomainBatchCreateDefaults} from 'features/domain/lib/domainMonitorFormHelper';
import {useIsFreeTariff, fetchMe, userSelector} from 'entities/user';
import {TypedDispatch} from 'shared/model/types';
import {isTranslate} from 'shared/lib/lang';

import CreateDomainMonitorType from './DomainMonitor/CreateDomainMonitorType';

export interface IUseDomainMonitorBatchCreateForm {
    form: UseFormReturn<CreateDomainMonitorType>,
    result: IBatchResult | undefined,
    isLoading: boolean,
    progress: number,
    freeTariff: boolean,
    submit: ReturnType<UseFormReturn<CreateDomainMonitorType>['handleSubmit']>,
    handleCloseResult: () => void,
}

export const useDomainMonitorBatchCreateForm = (
    handleClose: () => void,
    onShowResultChange?: (_isShowing: boolean) => void,
): IUseDomainMonitorBatchCreateForm => {
    const dispatch = useDispatch<TypedDispatch>();
    const user = useSelector(userSelector);
    const freeTariff = useIsFreeTariff();

    const {result, showResult, resetResult} = useBatchResult(onShowResultChange);
    const {trigger, isLoading, progress} = useBatchOperation();
    const {refetch: refetchTags} = tagsAPI.useGetTagsQuery();

    const form = useForm<CreateDomainMonitorType>({
        mode: 'onChange',
        defaultValues: getDomainBatchCreateDefaults(user),
    });

    const onSubmit: SubmitHandler<CreateDomainMonitorType> = async(values) => {
        await trigger(
            ApiUrls.DOMAIN_MONITORS_BATCH_ACTION('create'),
            'POST',
            buildDomainMonitorSettings(values, isTranslate()),
            (batchResult) => {
                dispatch(domainMonitorAPI.util.invalidateTags([
                    {type: 'DomainMonitor', id: 'LIST'},
                    'SummaryDomainMonitors',
                ]));
                refetchTags();
                dispatch(fetchMe());
                showResult(batchResult);
            },
        );
    };

    const handleCloseResult = () => {
        handleClose();
        resetResult();
    };

    return {
        form,
        result,
        isLoading,
        progress,
        freeTariff,
        submit: form.handleSubmit(onSubmit),
        handleCloseResult,
    };
};

export default useDomainMonitorBatchCreateForm;
